import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Loader2, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { pentestsAPI } from '../../services/api';

/**
 * Per-target exploitation levels.
 *
 * A level answers "how far may an autonomous agent go against THIS host". The
 * engagement carries a floor and any single target can override it, which is
 * what lets one engagement cover a production apex and an unreleased subdomain
 * without splitting into two.
 *
 * The chip always shows the EFFECTIVE level, computed by the backend, never the
 * stored override. A target inheriting `confirm` and a target pinned to
 * `confirm` behave identically today but diverge the moment the floor moves,
 * so the chip shows the level and a separate marker shows whether it is
 * inherited.
 */

export const LEVELS = [
  {
    value: 'confirm',
    label: 'Confirm',
    icon: ShieldCheck,
    chip: 'bg-wasabi-50 text-wasabi-700 border-wasabi-200',
    dot: 'bg-wasabi-500',
    blurb: 'Build the oracle and prove the flaw exists. Read nothing back.',
  },
  {
    value: 'prove',
    label: 'Prove',
    icon: ShieldQuestion,
    chip: 'bg-amber-50 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
    blurb: 'Read back one bounded, non-sensitive value as demonstrated impact.',
  },
  {
    value: 'exploit',
    label: 'Exploit',
    icon: ShieldAlert,
    chip: 'bg-red-50 text-red-800 border-red-200',
    dot: 'bg-red-500',
    blurb: 'Destructive-capability probes permitted. Never point this at production.',
  },
];

/**
 * What it costs to be wrong about a target.
 *
 * ORTHOGONAL TO THE LEVEL, and keeping them apart is the point. Level is how
 * far an agent may go; sensitivity is the consequence of going too far. It
 * never refuses an action: it raises the friction of raising the level, because
 * incident validation on production is real recurring work and a system that
 * refuses it gets bypassed with a shell script, after which nothing is
 * recorded at all.
 *
 * THERE WAS NO CONTROL FOR THIS ANYWHERE IN THE UI. The endpoint, the audit
 * entry and a `suggested_sensitivity` on every target response have existed
 * since the scope rows were built, and the suggestion's own docstring calls it
 * "a SUGGESTION for the UI to pre-select". Nothing read it, so every target
 * ever created sat at `unknown`, which renders to the agents as "sensitivity
 * not recorded" and once led a Lead to write "marked production" into a report
 * to fill the gap.
 */
export const SENSITIVITIES = [
  { value: 'beta', label: 'Beta', blurb: 'Pre-release. Being wrong here is cheap.' },
  { value: 'staging', label: 'Staging', blurb: 'Non-production, but someone depends on it.' },
  {
    value: 'production',
    label: 'Production',
    blurb: 'Real customers. Anything above Confirm costs a written reason and is disclosed in the report.',
  },
  {
    value: 'unknown',
    label: 'Not recorded',
    blurb: 'Nobody has said. The agents are told this is not a claim either way.',
  },
];

const levelMeta = (value) => LEVELS.find((l) => l.value === value) || LEVELS[0];
const sensitivityMeta = (value) =>
  SENSITIVITIES.find((s) => s.value === value) || SENSITIVITIES[3];

/**
 * WAS: take the operator to the floor control, which lived in another section.
 * The control now lives HERE, at the top of this section, so this scrolls within
 * the section instead of across the page.
 *
 * The floor and the per-target levels are one decision split across two places:
 * the floor sets what every inherited target does, and it is edited in Rules of
 * Engagement while the overrides are edited here. Showing the floor here
 * without a way to reach it was the confusing half of that.
 *
 * Coupled through a documented id rather than lifted into shared state, because
 * the two sections save differently: an override is written immediately, the
 * floor is part of the Pre-Engagement form and needs its Save.
 */
export const FLOOR_CONTROL_ID = 'roe-exploit-level-floor';

/**
 * The floor, edited where the levels are.
 *
 * It used to sit in Rules of Engagement under "Agentic policy", three sections
 * away from the per-target overrides it governs, and this component carried a
 * "change the floor" link whose only job was to scroll the operator there. One
 * decision split across two places, with a jump link as the seam.
 *
 * NOT REMOVED, which was the other option considered and rejected: report.py
 * discloses the floor in the deliverable, so a report would have stated a level
 * nobody could see or choose.
 *
 * Still saved by the Pre-Engagement form, because that is the form it belongs
 * to. The hint says so, since a select that looks live but needs a Save
 * elsewhere is worse than one that admits it.
 */
function FloorControl({ floor, onChange, isPentester, dirty }) {
  if (!onChange) return null;
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <label
        htmlFor={FLOOR_CONTROL_ID}
        className="block text-xs font-semibold uppercase tracking-wide text-slate-500"
      >
        Engagement floor
      </label>
      <div className="mt-1.5 flex flex-wrap items-center gap-3">
        <select
          id={FLOOR_CONTROL_ID}
          value={floor}
          onChange={(e) => onChange(e.target.value)}
          disabled={!isPentester}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500 disabled:bg-slate-50"
        >
          {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
        <p className="text-[11px] leading-snug text-slate-500">
          Every target below with no level of its own follows this.
          {dirty && (
            <span className="ml-1 font-semibold text-amber-700">
              Save Pre-Engagement to apply.
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

function focusFloorControl() {
  const el = document.getElementById(FLOOR_CONTROL_ID);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // Focus after the scroll settles; focusing first fights the smooth scroll.
  window.setTimeout(() => el.focus({ preventScroll: true }), 350);
}

/**
 * The chip is a CONTROL, and it has to look like one.
 *
 * It read as a status badge: a coloured pill, pinned to the far right of the
 * row, with nothing to suggest clicking it did anything. Operators took it for
 * a label. The caret is the fix, together with a border that darkens on hover
 * and a chip that sits next to the target it describes rather than a column
 * away from it.
 *
 * The colour still encodes the level, because that is what makes a scope list
 * readable at a glance, so the affordance had to be added without taking the
 * colour away.
 */
function LevelChip({ level, inherited, onClick, disabled, open }) {
  const meta = levelMeta(level);
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-haspopup="true"
      aria-expanded={!!open}
      title={
        disabled
          ? `${meta.label}${inherited ? ', inherited from the engagement floor' : ''}`
          : 'Click to change how far an agent may go against this target'
      }
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition
        ${meta.chip}
        ${disabled
          ? 'opacity-60 cursor-default'
          : 'cursor-pointer hover:brightness-95 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-wasabi-400'}
        ${open ? 'ring-2 ring-wasabi-400' : ''}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
      {inherited && <span className="font-normal opacity-70">(inherited)</span>}
      {!disabled && <ChevronDown className={`h-3 w-3 opacity-60 transition ${open ? 'rotate-180' : ''}`} />}
    </button>
  );
}

function LevelPopover({ target, floor, onPick, onSensitivity, onClose, busy }) {
  const [authorityRef, setAuthorityRef] = useState(target.level_authority_ref || '');

  return (
    <div className="absolute left-0 top-full z-30 mt-2 w-80 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Exploitation level
      </div>
      <div className="mb-1 truncate font-mono text-xs text-slate-900">{target.value}</div>
      <div className="mb-3 text-xs leading-snug text-slate-500">
        The engagement floor is{' '}
        <span className="font-semibold">{levelMeta(floor).label}</span>.
        {target.exploit_level
          ? ' This target overrides it.'
          : ' This target follows it, so moving the floor moves this target too.'}
        <button
          type="button"
          onClick={focusFloorControl}
          className="mt-1 block font-medium text-wasabi-700 hover:underline"
        >
          Change the floor, at the top of this section
        </button>
      </div>

      <div className="space-y-1.5">
        {LEVELS.map((l) => {
          const Icon = l.icon;
          const active = target.exploit_level === l.value;
          return (
            <button
              key={l.value}
              type="button"
              disabled={busy}
              onClick={() => onPick(l.value, authorityRef)}
              className={`flex w-full items-start gap-2 rounded-md border p-2 text-left transition
                ${active ? 'border-wasabi-400 bg-wasabi-50' : 'border-slate-200 hover:bg-slate-50'}`}
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                  {l.label}
                  {active && <Check className="h-3.5 w-3.5 text-wasabi-600" />}
                </span>
                <span className="block text-xs leading-snug text-slate-500">{l.blurb}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-3 border-t border-slate-100 pt-3">
        <label className="block text-xs font-medium text-slate-600">
          Sensitivity
          <span className="ml-1 font-normal text-slate-400">
            what it costs to be wrong here
          </span>
        </label>
        <select
          value={target.sensitivity || 'unknown'}
          disabled={busy}
          onChange={(e) => onSensitivity(e.target.value)}
          className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs
                     focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
        >
          {SENSITIVITIES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <p className="mt-1 text-[11px] leading-snug text-slate-400">
          {sensitivityMeta(target.sensitivity).blurb}
        </p>
        {/* The backend's own guess, offered rather than applied. It errs toward
            production on anything it does not recognise, and a flag that gates
            a real decision must not be set silently from a hostname. */}
        {(!target.sensitivity || target.sensitivity === 'unknown')
          && target.suggested_sensitivity
          && target.suggested_sensitivity !== 'unknown' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onSensitivity(target.suggested_sensitivity)}
            className="mt-1 text-[11px] font-medium text-wasabi-700 hover:underline disabled:opacity-40"
          >
            Set to {sensitivityMeta(target.suggested_sensitivity).label}, suggested from the hostname
          </button>
        )}
      </div>

      <label className="mt-3 block text-xs font-medium text-slate-600">
        Authority reference <span className="font-normal text-slate-400">(optional)</span>
      </label>
      <input
        value={authorityRef}
        onChange={(e) => setAuthorityRef(e.target.value)}
        placeholder="Slack thread, Jira key, who signed off"
        className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs
                   focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
      />
      <p className="mt-1 text-[11px] leading-snug text-slate-400">
        Leave empty when you are the one making the call. Recorded in the audit trail either way.
      </p>

      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          disabled={busy || !target.exploit_level}
          onClick={() => onPick(null, authorityRef)}
          className="text-xs font-medium text-slate-500 hover:text-slate-800 disabled:opacity-40"
        >
          Clear override, inherit the floor
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-xs font-medium text-slate-500 hover:text-slate-800"
        >
          Close
        </button>
      </div>
    </div>
  );
}

export default function ScopeTargetLevels({
  pentestId, isPentester, refreshKey, floor: floorDraft, onFloorChange, floorDirty,
}) {
  const [scope, setScope] = useState(null);
  const [error, setError] = useState(null);
  const [openFor, setOpenFor] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setError(null);
      setScope(await pentestsAPI.scope.get(pentestId));
    } catch (e) {
      // A Wasabian gets a 403 here by design; say so rather than showing an
      // empty scope, which would read as "nothing is in scope".
      setError(
        e?.response?.status === 403
          ? 'Exploitation levels are visible to Red Teamers only.'
          : (e?.response?.data?.detail || 'Could not load scope levels.'),
      );
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const floor = scope?.roe?.exploit_level_floor || 'confirm';

  const counts = useMemo(() => {
    const out = { confirm: 0, prove: 0, exploit: 0 };
    (scope?.targets || []).forEach((t) => { out[t.effective_level] = (out[t.effective_level] || 0) + 1; });
    return out;
  }, [scope]);

  const setSensitivity = async (target, sensitivity) => {
    setBusyId(target.id);
    try {
      await pentestsAPI.scope.setSensitivity(pentestId, target.id, sensitivity);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not change the sensitivity.');
    } finally {
      setBusyId(null);
    }
  };

  const setLevel = async (target, level, authorityRef) => {
    setBusyId(target.id);
    try {
      await pentestsAPI.scope.setLevel(pentestId, target.id, {
        exploit_level: level,
        authority_ref: authorityRef || null,
      });
      await load();
      setOpenFor(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not change the level.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading exploitation levels...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  const targets = scope?.targets || [];

  if (!targets.length) {
    // The floor still renders. Moving the control here would otherwise make it
    // unreachable on a fresh engagement, which is exactly when it is set.
    return (
      <div>
        <FloorControl
          floor={floorDraft ?? floor}
          onChange={onFloorChange}
          isPentester={isPentester}
          dirty={floorDirty}
        />
        <p className="text-sm text-slate-500">
          No scope targets yet. Add URLs, IPs or hostnames above and they will appear here with a level.
        </p>
      </div>
    );
  }

  return (
    <div>
      <FloorControl
        floor={floorDraft ?? floor}
        onChange={onFloorChange}
        isPentester={isPentester}
        dirty={floorDirty}
      />
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        {/* No "change the floor" link any more: the control is directly above.
            The summary states the SAVED floor, which is what the backend is
            actually enforcing, and may differ from the draft in the select. */}
        <span>
          Floor <span className="font-semibold text-slate-700">{levelMeta(floor).label}</span>
        </span>
        {LEVELS.map((l) => (
          <span key={l.value} className="inline-flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${l.dot}`} />
            {counts[l.value] || 0} {l.label}
          </span>
        ))}
        <span className="ml-auto">{targets.length} target{targets.length === 1 ? '' : 's'}</span>
      </div>

      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {targets.map((t) => (
          <div key={t.id} className="relative flex items-center gap-3 px-3 py-2">
            <span className="w-16 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {t.kind}
            </span>
            <span
              className="min-w-0 max-w-[22rem] truncate font-mono text-xs text-slate-900"
              title={t.value}
            >
              {t.value}
            </span>
            {/* Next to the target rather than at the row's right edge. A control
                a column away from its subject reads as a status column. */}
            <span className="relative shrink-0">
              {busyId === t.id
                ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                : (
                  <LevelChip
                    level={t.effective_level}
                    inherited={t.inherited}
                    disabled={!isPentester}
                    open={openFor === t.id}
                    onClick={() => setOpenFor(openFor === t.id ? null : t.id)}
                  />
                )}
              {openFor === t.id && isPentester && (
                <LevelPopover
                  target={t}
                  floor={floor}
                  busy={busyId === t.id}
                  onPick={(level, ref) => setLevel(t, level, ref)}
                  onSensitivity={(value) => setSensitivity(t, value)}
                  onClose={() => setOpenFor(null)}
                />
              )}
            </span>
            {/* Visible on the row, not only behind the popover. Every target
                ever created sat at `unknown` because nothing surfaced it, and an
                unset flag that gates a production raise should not need a click
                to notice. */}
            <button
              type="button"
              disabled={!isPentester}
              onClick={() => setOpenFor(openFor === t.id ? null : t.id)}
              title={sensitivityMeta(t.sensitivity).blurb}
              className={`shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-medium
                ${t.sensitivity === 'production'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : (!t.sensitivity || t.sensitivity === 'unknown')
                    ? 'border-dashed border-slate-300 text-slate-400'
                    : 'border-slate-200 bg-slate-50 text-slate-600'}
                ${isPentester ? 'hover:border-wasabi-400' : 'cursor-default'}`}
            >
              {sensitivityMeta(t.sensitivity).label}
            </button>
            {t.level_authority_ref && (
              <span
                className="ml-auto hidden max-w-[10rem] truncate text-[11px] text-slate-400 sm:inline"
                title={`Authority: ${t.level_authority_ref}`}
              >
                {t.level_authority_ref}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
